import { assertWallLengthLockTransition } from './wallLengthLocks.js';
import clipping from 'polygon-clipping';
import { blankDoc, uid } from './constants.js';
import { ptInPoly, distToSeg, segHit } from './geometry.js';

export const DEFAULT_BUILDING = 'building-main';
export const buildingID = plan => plan.buildingId || DEFAULT_BUILDING;
export const siteBuildings = S => S.buildings?.length ? S.buildings : [{ id: DEFAULT_BUILDING, name: 'Main building' }];
export const siteLevels = S => S._siteLevels || [{ id: S.activeFloorId || 'ground', name: S.floorName || 'Ground floor', elevation: S.elevation || 0, buildingId: buildingID(S), plan: S },
  ...(S.floors || []).map(f => ({ ...f, buildingId: f.buildingId || buildingID(f.plan) }))];
export const buildingFloors = (S, id = buildingID(S)) => siteLevels(S).filter(f => f.buildingId === id).sort((a, b) => a.elevation - b.elevation || a.id.localeCompare(b.id));

export function registerBuildings(S) {
  S.buildings = structuredClone(siteBuildings(S));
  S.buildingId = buildingID(S);
  for (const f of S.floors || []) { f.buildingId ||= buildingID(f.plan); f.plan.buildingId = f.buildingId; }
  return S;
}

export function floorSnapshot(S) {
  const { floors, stairs, lighting, buildings, ...plan } = structuredClone(S);
  return { id: plan.activeFloorId, name: plan.floorName || 'Ground floor', elevation: plan.elevation || 0, buildingId: buildingID(plan), plan };
}

export function activateSiteLevel(S, id) {
  const target = S.floors.find(f => f.id === id);
  if (!target) return S;
  return { ...blankDoc(), ...structuredClone(target.plan), stairs: S.stairs, lighting: S.lighting,
    ...(S.buildings ? { buildings: S.buildings } : {}), buildingId: target.buildingId || buildingID(target.plan),
    activeFloorId: target.id, floorName: target.name, elevation: target.elevation,
    floors: [...S.floors.filter(f => f.id !== id), floorSnapshot(S)] };
}

const polygon = a => [a.pts.map(p => [p.x, p.y])];
const contains = (a, p) => ptInPoly(p.x, p.y, a.pts) || a.pts.some((v, i) => { const w = a.pts[(i + 1) % a.pts.length]; return distToSeg(p.x, p.y, v.x, v.y, w.x, w.y) < 0.001; });
const overlaps = (a, b) => clipping.intersection(polygon(a), polygon(b)).length > 0;
const touches = (a, b) => overlaps(a, b) || a.pts.some(p => contains(b, p)) || b.pts.some(p => contains(a, p));
export const buildingAreas = S => (S.areas || []).filter(a => a.kind === 'floor' && a.environment !== 'outdoor' &&
  (a.purpose === 'building' || a.roofHeight > 0 || (S.walls || []).some(w => a.pts.some((p, i) => {
    const q = a.pts[(i + 1) % a.pts.length];
    return distToSeg(w.x1, w.y1, p.x, p.y, q.x, q.y) < 0.001 && distToSeg(w.x2, w.y2, p.x, p.y, q.x, q.y) < 0.001;
  }))));

/** Split only after explicit review. Never infer ownership from a bounding box:
 * angled and concave footprints use their actual polygons. Ambiguous crossing
 * walls and equipment connections stop the operation before any source changes. */
export function separateBuilding(source, areaID, name) {
  const S = registerBuildings(structuredClone(source)), owner = buildingID(S);
  if (S.buildings.length >= 32) throw new Error('This Site already has 32 buildings.');
  const seed = buildingAreas(S).find(a => a.id === areaID);
  if (!seed) throw new Error('Choose an enclosed room or building footprint first.');
  const connected = (areas, seeds) => {
    const selected = new Set(seeds);
    let more = true;
    while (more) { more = false; for (const a of areas) if (!selected.has(a) && [...selected].some(b => touches(a, b))) { selected.add(a); more = true; } }
    return [...selected];
  };
  const footprint = connected(buildingAreas(S), [seed]);
  const id = uid('building'), moved = [], floorMap = new Map(), summary = [];
  for (const level of buildingFloors(S, owner)) {
    const plan = level.id === S.activeFloorId ? S : S.floors.find(f => f.id === level.id).plan;
    const candidates = buildingAreas(plan);
    const areas = connected(candidates, candidates.filter(a => footprint.some(b => overlaps(a, b) || a === seed)));
    if (!areas.length) continue;
    const inside = p => areas.some(a => contains(a, p));
    const allInside = pts => pts.length && pts.every(inside);
    const next = { ...blankDoc(), opt: structuredClone(plan.opt), install: structuredClone(plan.install), planning: structuredClone(plan.planning),
      setup: structuredClone(plan.setup), bg: structuredClone(plan.bg), sceneState: plan.sceneState, buildingId: id,
      activeFloorId: uid('floor'), floorName: level.name, elevation: level.elevation };
    const transfer = (key, predicate) => {
      next[key] = (plan[key] || []).filter(predicate);
      plan[key] = (plan[key] || []).filter(v => !next[key].includes(v));
    };
    const containedWalls = new Set();
    for (const w of plan.walls || []) {
      const cuts = [0,1];
      for (const area of areas) for (let i=0;i<area.pts.length;i++) {
        const a=area.pts[i], b=area.pts[(i+1)%area.pts.length];
        const t=segHit(w.x1,w.y1,w.x2,w.y2,a.x,a.y,b.x,b.y);
        if (t !== null) cuts.push(t);
      }
      cuts.sort((a,b)=>a-b);
      const states = cuts.slice(1).flatMap((t,i)=>t-cuts[i] > 1e-8 ? [inside({x:w.x1+(w.x2-w.x1)*(t+cuts[i])/2,y:w.y1+(w.y2-w.y1)*(t+cuts[i])/2})] : []);
      if (states.some(Boolean) && states.some(v=>!v)) throw new Error('A wall crosses the proposed building boundary. Split that wall before separating the building.');
      if (states.length && states.every(Boolean)) containedWalls.add(w);
    }
    transfer('walls', w => containedWalls.has(w));
    const wallIDs = new Set(next.walls.map(w => w.id));
    transfer('wallLengthLocks', lock => wallIDs.has(lock.wallId));
    transfer('openings', o => wallIDs.has(o.wallId));
    transfer('areas', a => areas.includes(a) || a.environment !== 'outdoor' && allInside(a.pts));
    transfer('zones', a => allInside(a.pts));
    for (const key of ['cams', 'equipment', 'targets', 'lights', 'tasks', 'obstacles']) transfer(key, inside);
    const cameraIDs = new Set(next.cams.map(c => c.id)), equipmentIDs = new Set(next.equipment.map(e => e.id));
    for (const c of plan.cables || []) if (cameraIDs.has(c.camId) !== equipmentIDs.has(c.equipmentId)) throw new Error('A cable connects this building to equipment outside it. Keep that connected layout together until the cable is reassigned.');
    for (const c of [...next.cams, ...plan.cams]) if (c.recorderId && cameraIDs.has(c.id) !== equipmentIDs.has(c.recorderId)) throw new Error('A camera uses a recorder outside this building. Reassign it before separating the building.');
    transfer('cables', c => cameraIDs.has(c.camId) && equipmentIDs.has(c.equipmentId));
    transfer('accessories', a => cameraIDs.has(a.camId));
    next.models = structuredClone((plan.models || []).filter(m => next.cams.some(c => c.modelId === m.id)));
    plan.review = null;
    floorMap.set(level.id, next.activeFloorId);
    moved.push(floorSnapshot(next)); summary.push({ name: level.name, rooms: next.areas.filter(a => a.kind === 'floor').length, walls: next.walls.length, elevation: level.elevation });
  }
  if (S.floors.length + moved.length > 127) throw new Error('This Site would exceed 128 floor plans.');
  for (const stair of S.stairs || []) if (footprint.some(a => contains(a, { x: stair.x, y: stair.y }))) {
    if (!floorMap.has(stair.fromFloorId) || !floorMap.has(stair.toFloorId)) throw new Error('A stair crosses the proposed building boundary. Review its connected floors first.');
    stair.fromFloorId = floorMap.get(stair.fromFloorId); stair.toFloorId = floorMap.get(stair.toFloorId);
  }
  S.buildings.push({ id, name: name.trim() || 'New building' }); S.floors.push(...moved);
  const doc = activateSiteLevel(S, moved.find(f => f.elevation === source.elevation)?.id || moved[0].id);
  assertWallLengthLockTransition(source, doc);
  return { doc, summary };
}
